/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import {
  Observable,
  Subject,
  BehaviorSubject,
  Subscription,
  timer,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeneralService } from './general.service';
import { API } from '../configs/api.config';
import {
  SSENotificationMessage,
  SSEConnectionConfig,
  SSEConnectionStatus,
  NotificationType,
  NotificationPriority,
} from '../models/notification.model';
import { environment } from 'projects/sharing/environments/environment';
import { PermissionContextService } from './permission-context.service';
import { LS } from '../enums/local-storage.enum';

@Injectable({
  providedIn: 'root',
})
export class SSENotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly notificationSubject = new Subject<SSENotificationMessage>();
  private readonly connectionStatusSubject =
    new BehaviorSubject<SSEConnectionStatus>({
      isConnected: false,
      reconnectAttempts: 0,
    });

  private reconnectAttempts = 0;
  private reconnectDelay = 5000; // 5 seconds
  private readonly maxReconnectDelay = 60_000; // 60 seconds cap
  private currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private eventSource: EventSource | null = null;
  private subscriptions: Subscription[] = [];
  // Set to true by stopSSEConnection() so that in-flight read/fetch
  // rejections and the reconnect timer do not bounce a new connection
  // back up after a deliberate stop. Reset by startSSEConnection().
  private isStopped = false;

  private readonly permissionContext = inject(PermissionContextService);

  constructor(
    private readonly generalService: GeneralService,
    private readonly zone: NgZone,
  ) {}

  /**
   * Start SSE connection (authentication handled by interceptor)
   * @param config - Optional configuration for connection
   */
  startSSEConnection(
    config?: SSEConnectionConfig,
  ): Observable<SSENotificationMessage> {
    if (config) {
      this.reconnectDelay = config.reconnectDelay || this.reconnectDelay;
    }

    this.isStopped = false;
    this.connectSSE();
    return this.notificationSubject.asObservable();
  }

  /**
   * Get connection status as Observable
   */
  getConnectionStatus(): Observable<SSEConnectionStatus> {
    return this.connectionStatusSubject.asObservable();
  }

  /**
   * Stop SSE connection and cleanup resources
   */
  stopSSEConnection(): void {
    this.isStopped = true;
    this.closeConnection();
  }

  /**
   * Connect to SSE endpoint using GeneralService
   */
  private connectSSE(): void {
    if (this.isConnected() || this.isStopped) {
      return;
    }

    this.initializeEventSource();
  }

  /**
   * Initialize EventSource for SSE connection
   */
  private initializeEventSource(): void {
    const sseUrl = `${API.NOTIFICATION_SSE_BASE_URL}${API.NOTIFICATION_SSE_STREAM}`;
    const pair = this.permissionContext.getLoginPair();

    const clientInfo = this.getClientInfo();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Api-Key': environment.X_API_KEY,
      'X-Menu-Id': pair.menuId,
      'X-Action-Id': pair.actionId,
      'X-User-Ip': clientInfo.IP,
      'X-User-Device': clientInfo.DeviceStr,
      'X-User-Location': clientInfo.LocationStr,
    };

    fetch(sseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    })
      .then((response) => {
        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        this.zone.run(() => {
          this.updateConnectionStatus(true, 0);
          this.resetReconnectionAttempts();
        });

        this.currentReader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const read = (): void => {
          if (!this.currentReader) {
            return;
          }

          this.currentReader
            .read()
            .then(({ done, value }) => {
              if (done) {
                this.zone.run(() => {
                  this.updateConnectionStatus(
                    false,
                    this.reconnectAttempts,
                    'Stream ended',
                  );
                  this.handleReconnection();
                });
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data:')) {
                  const eventData = line.substring(5).trim();
                  if (eventData) {
                    this.zone.run(() => {
                      this.processSSEEvent(eventData);
                    });
                  }
                }
              }

              read();
            })
            .catch((error) => {
              this.zone.run(() => {
                this.updateConnectionStatus(
                  false,
                  this.reconnectAttempts,
                  error instanceof Error ? error.message : 'Read error',
                );
                this.handleReconnection();
              });
            });
        };

        read();
      })
      .catch((error) => {
        this.zone.run(() => {
          this.updateConnectionStatus(
            false,
            this.reconnectAttempts,
            error instanceof Error ? error.message : 'SSE connection failed',
          );
          this.handleReconnection();
        });
      });
  }

  /**
   * Process individual SSE event
   */
  private processSSEEvent(eventData: string): void {
    try {
      const parsedData = JSON.parse(eventData);

      const notification: SSENotificationMessage = {
        title: parsedData.title || parsedData.Title || 'Notification',
        type: this.normalizeNotificationType(
          parsedData.type || parsedData.Type,
        ),
        priority: this.normalizeNotificationPriority(
          parsedData.priority || parsedData.Priority,
        ),
      };

      this.notificationSubject.next(notification);
    } catch (error) {
      // Silent catch
    }
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnection(): void {
    if (this.isStopped) {
      return;
    }
    this.closeConnection();
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );
    this.updateConnectionStatus(false, this.reconnectAttempts);

    const reconnectSub = timer(delay)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.connectSSE();
      });

    this.subscriptions.push(reconnectSub);
  }

  /**
   * Update connection status
   */
  private updateConnectionStatus(
    isConnected: boolean,
    attempts: number,
    error?: string,
  ): void {
    this.connectionStatusSubject.next({
      isConnected,
      reconnectAttempts: attempts,
      lastError: error,
      // lastUpdate: new Date(),
    });
  }

  /**
   * Close connection and cleanup resources
   */
  private closeConnection(): void {
    // Close EventSource if exists
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // Cancel current reader if exists
    if (this.currentReader) {
      this.currentReader.cancel();
      this.currentReader = null;
    }

    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Update connection status
    this.updateConnectionStatus(false, 0);
    this.resetReconnectionAttempts();
  }

  /**
   * Normalize notification type
   */
  private normalizeNotificationType(type: any): NotificationType {
    if (!type) return NotificationType.INFO;

    const typeStr = type.toString().toLowerCase();
    switch (typeStr) {
      case 'success':
        return NotificationType.SUCCESS;
      case 'error':
        return NotificationType.ERROR;
      case 'warning':
        return NotificationType.WARNING;
      default:
        return NotificationType.INFO;
    }
  }

  /**
   * Normalize notification priority
   */
  private normalizeNotificationPriority(priority: any): NotificationPriority {
    if (!priority) return NotificationPriority.MEDIUM;

    const priorityStr = priority.toString().toLowerCase();
    switch (priorityStr) {
      case 'high':
        return NotificationPriority.HIGH;
      case 'low':
        return NotificationPriority.LOW;
      default:
        return NotificationPriority.MEDIUM;
    }
  }

  /**
   * Reset reconnection attempts
   */
  resetReconnectionAttempts(): void {
    this.reconnectAttempts = 0;
    this.updateConnectionStatus(this.isConnected(), 0);
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.connectionStatusSubject.value.isConnected;
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeConnection();
    this.notificationSubject.complete();
    this.connectionStatusSubject.complete();
  }

  private getClientInfo(): {
    IP: string;
    DeviceStr: string;
    LocationStr: string;
  } {
    const clientInfoStr = localStorage.getItem(LS.CLIENT_INFO);
    if (clientInfoStr) {
      try {
        const parsed = JSON.parse(clientInfoStr);
        return {
          IP: parsed.IP || '-',
          DeviceStr: parsed.DeviceStr || '-',
          LocationStr: parsed.LocationStr || '-',
        };
      } catch {
        // fall through
      }
    }
    return { IP: '-', DeviceStr: '-', LocationStr: '-' };
  }
}
